import { useCallback, useEffect, useMemo } from "react";
import { BrowserRouter, useNavigate } from "react-router";

import { APP_ROUTES } from "@/core/constants";
import { AppRoute, type AppRouteDefinition } from "@/core/router";
import { AppShell } from "@/core/ui/molecules";

import { accessPresetsRoutes } from "../../app/access-presets/router";
import { auditRoutes } from "../../app/audit/router";
import { authRoutes } from "../../app/auth/router";
import { dashboardRoutes } from "../../app/dashboard/router";
import { libraryRoutes } from "../../app/library/router";
import { plansRoutes } from "../../app/plans/router";
import { tenantsRoutes } from "../../app/tenants/router";
import { AuthStore } from "../data_sources/store/auth";
import { useStore } from "../utils/hooks";

const DEFAULT_ROUTE_PATH = APP_ROUTES.superAdmin.dashboard.path;

const appRoutes: readonly AppRouteDefinition[] = [
  ...authRoutes,
  ...dashboardRoutes,
  ...tenantsRoutes,
  ...libraryRoutes,
  ...plansRoutes,
  ...accessPresetsRoutes,
  ...auditRoutes,
] as const;

function AppRoutes() {
  const { state: authState, isConnected } = useStore(AuthStore.instance);
  const isAuthorized: boolean = useMemo(
    () => !!authState?.auth?.isValidToken(),
    [authState],
  );
  const navigate = useNavigate();

  const handleSignOut = useCallback(() => {
    AuthStore.instance.actions.signOut().subscribe(() => {
      navigate(APP_ROUTES.auth.signIn.path, { replace: true });
    });
  }, [navigate]);
  useEffect(() => {
    if (isConnected && !isAuthorized) {
      navigate(APP_ROUTES.auth.signIn.path, { replace: true });
    }
  }, [isConnected, isAuthorized, navigate]);

  return (
    <AppRoute
      fallbackPath={DEFAULT_ROUTE_PATH}
      layoutElement={<AppShell onSignOut={handleSignOut} />}
      routes={appRoutes}
    />
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
